/**
 * Table export utilities for CSV format
 * Provides export functionality for DataTable data
 */

import { saveAs } from "file-saver";
import Papa from "papaparse";
import type { Column } from "@/components/DataTable/DataTable.types";

export interface TableExportMetadata {
  /** Base filename for the exported file (without extension) */
  filename?: string;
  /** Whether to include timestamp in filename. Defaults to true */
  timestamp?: boolean;
}

export interface TableExportOptions<T> {
  /** Column definitions for the table */
  columns: Column<T>[];
  /** Data rows to export */
  data: T[];
  /** Export metadata */
  metadata?: TableExportMetadata;
}

/**
 * Generates a filename with optional timestamp.
 */
function generateFilename(baseFilename: string, extension: string, includeTimestamp: boolean = true): string {
  const base = baseFilename || "table-data";
  if (includeTimestamp) {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
    return `${base}-${timestamp}.${extension}`;
  }
  return `${base}.${extension}`;
}

/**
 * Get the value from a row based on column accessor
 */
function getCellValue<T>(row: T, column: Column<T>): unknown {
  if (column.accessor) {
    if (typeof column.accessor === "function") {
      return column.accessor(row);
    }
    return row[column.accessor as keyof T];
  }
  return null;
}

/**
 * Get the exportable value for a cell
 */
function getExportValue<T>(column: Column<T>, row: T): string | number | boolean {
  const rawValue = getCellValue(row, column);

  if (column.exportValue) {
    const exportedValue = column.exportValue(rawValue, row);
    return exportedValue ?? "";
  }

  // Default conversion
  if (rawValue === null || rawValue === undefined) {
    return "";
  }

  if (rawValue instanceof Date) {
    return rawValue.toISOString();
  }

  if (typeof rawValue === "object") {
    return JSON.stringify(rawValue);
  }

  return rawValue as string | number | boolean;
}

/**
 * Get columns that should be included in export
 */
function getExportableColumns<T>(columns: Column<T>[]): Column<T>[] {
  return columns.filter((col) => col.exportable !== false);
}

/**
 * Validate that export headers are unique.
 * Throws an error if duplicate headers are found to prevent silent data loss.
 */
function validateUniqueHeaders<T>(columns: Column<T>[]): void {
  const headers = columns.map((col) => col.exportLabel || col.label);
  const seen = new Set<string>();
  const duplicates: string[] = [];

  for (const header of headers) {
    if (seen.has(header)) {
      if (!duplicates.includes(header)) {
        duplicates.push(header);
      }
    } else {
      seen.add(header);
    }
  }

  if (duplicates.length > 0) {
    throw new Error(
      `Duplicate export headers found: ${duplicates.join(", ")}. Use exportLabel to provide unique headers.`
    );
  }
}

/**
 * Export table data as CSV
 *
 * @param options - Export configuration
 * @returns Promise that resolves when export is complete
 *
 * @example
 * ```typescript
 * await exportTableAsCSV({
 *   columns: tableColumns,
 *   data: tableData,
 *   metadata: {
 *     filename: "devices-export",
 *     timestamp: true
 *   }
 * });
 * ```
 */
export async function exportTableAsCSV<T>(options: TableExportOptions<T>): Promise<void> {
  const { columns, data, metadata = {} } = options;

  try {
    const exportableColumns = getExportableColumns(columns);

    if (exportableColumns.length === 0) {
      throw new Error("No exportable columns found");
    }

    // Validate headers are unique to prevent silent data loss
    validateUniqueHeaders(exportableColumns);

    // Build rows with column headers
    const rows = data.map((row) => {
      const exportRow: Record<string, string | number | boolean> = {};

      for (const column of exportableColumns) {
        const header = column.exportLabel || column.label;
        exportRow[header] = getExportValue(column, row);
      }

      return exportRow;
    });

    // Generate CSV using papaparse
    const csv = Papa.unparse(rows, {
      header: true,
      skipEmptyLines: true,
    });

    // Create and download the file
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const filename = generateFilename(metadata.filename || "table-data", "csv", metadata.timestamp !== false);
    saveAs(blob, filename);
  } catch (error) {
    throw new Error(`CSV export failed: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

/**
 * Prepare table data for export (without triggering download)
 * Useful for server-side export where you need the transformed data
 *
 * @param options - Export configuration
 * @returns Array of row objects ready for CSV generation
 */
export function prepareTableDataForExport<T>(
  options: Omit<TableExportOptions<T>, "metadata">
): Record<string, string | number | boolean>[] {
  const { columns, data } = options;
  const exportableColumns = getExportableColumns(columns);

  // Validate headers are unique to prevent silent data loss
  validateUniqueHeaders(exportableColumns);

  return data.map((row) => {
    const exportRow: Record<string, string | number | boolean> = {};

    for (const column of exportableColumns) {
      const header = column.exportLabel || column.label;
      exportRow[header] = getExportValue(column, row);
    }

    return exportRow;
  });
}

/**
 * Get column headers for export
 * Useful for generating CSV headers on the server
 */
export function getExportHeaders<T>(columns: Column<T>[]): string[] {
  return getExportableColumns(columns).map((col) => col.exportLabel || col.label);
}
